/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to monitoring service
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Update state with error info
    this.setState({
      error,
      errorInfo
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, you might want to log to an error reporting service
    if (process.env.NODE_ENV === 'production') {
      // Example: logErrorToService(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div 
          className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#e3f2fd] to-[#f1f8e9]"
          data-ui-id="error-boundary"
          data-ui-props={JSON.stringify({
            type: 'errorBoundary',
            component: 'ErrorBoundary',
            hasError: true,
            error: this.state.error?.message
          })}
        >
          <div className="max-w-md w-full mx-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
              <div className="text-6xl mb-6">⚠️</div>
              
              <h1 className="text-2xl font-bold text-[#1565c0] dark:text-blue-400 mb-4">
                Something went wrong
              </h1>
              
              <p className="text-[#666] dark:text-gray-400 mb-6">
                We're sorry, but something unexpected happened. Please try again.
              </p>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
                  <h3 className="text-red-800 font-semibold mb-2">Error Details:</h3>
                  <p className="text-red-600 text-sm font-mono">
                    {this.state.error.message}
                  </p>
                  {this.state.errorInfo && (
                    <details className="mt-2">
                      <summary className="text-red-600 text-sm cursor-pointer">
                        Stack Trace
                      </summary>
                      <pre className="text-red-600 text-xs mt-2 overflow-auto">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              <div className="space-y-3">
                <Button type="button" className="w-full" onClick={this.handleRetry}>
                  Try Again
                </Button>
                
                <Button type="button" variant="outline" className="w-full" onClick={() => window.location.reload()}>
                  Reload Page
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Specialized error boundaries for different use cases
export const RouteErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundary fallback={<RouteErrorFallback />}>
    {children}
  </ErrorBoundary>
);

export const ComponentErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundary fallback={<ComponentErrorFallback />}>
    {children}
  </ErrorBoundary>
);

export const LazyLoadErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ErrorBoundary fallback={<LazyLoadErrorFallback />}>
    {children}
  </ErrorBoundary>
);

// Error fallback components
const RouteErrorFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#e3f2fd] to-[#f1f8e9]">
    <div className="text-center">
      <div className="text-6xl mb-6">🚫</div>
      <h1 className="text-2xl font-bold text-[#1565c0] dark:text-blue-400 mb-4">Page Error</h1>
      <p className="text-[#666] dark:text-gray-400 mb-6">This page encountered an error. Please try again.</p>
      <Button type="button" onClick={() => window.location.reload()}>
        Reload Page
      </Button>
    </div>
  </div>
);

const ComponentErrorFallback: React.FC = () => (
  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
    <div className="text-red-600 text-sm">
      <p className="font-semibold">Component Error</p>
      <p>This component failed to load. Please refresh the page.</p>
    </div>
  </div>
);

const LazyLoadErrorFallback: React.FC = () => (
  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 text-center">
    <div className="text-yellow-600 text-sm">
      <p className="font-semibold">Loading Error</p>
      <p>Failed to load component. Please try again.</p>
    </div>
  </div>
);

export default ErrorBoundary;